import RecordBase from './Base';
import dig from 'object-dig';
import { useTranslation } from 'react-i18next';
import onlyNumbers from '~/utils/Number/onlyNumbers';

export default class PersonRecord extends RecordBase {
  static defaultAttributes = {
    name: '',
    cell_phone: '',
    created_at: '',
    email: '',
    cpf: '',
    expires_at: '',
    born_date: null,
    id: null,
    last_access: '',
    uuid: '',
    image_base64: '',
    phone_country_prefix: '',
    platform: '',
    reason_to_use: '',
    packs: [],
    specialties: [],
    specialty_durations: {},
    user_activities: [],
    nationality: '',
    doctor_interest: '',
    device_language: '',
    store_location: '',
    app_version: '',
    partner_id: null,
    partner_supplier_id: null,
    password: '',
    role: 'customer',
    signup_messages: '',
    device_info: '',
    corporate_partner: null,
    partner_supplier: null,
    partner: null,
    owner_partner: null,
    dependent_owners: [],
    package_users: [],
    address: null,
    crm: '',
    crp: '',
    crm_numero: '',
    crm_conselho: 'CRM',
    crm_uf: '',
    specialties_packs: [],
    packs_packages: [],
    gender: '',
    policy_id: null,
    cns: null,
    has_benefits_club: false,
    enabled_benefits_club: false,
    is_regulator: false,
    reports_only: false,
  };

  static _attributeNames = null;

  static get attributeNames() {
    const { t } = useTranslation();
    if (!this._attributeNames) {
      this._attributeNames = {
        id: t('person.attributes.id'),
        name: t('person.attributes.name'),
        cell_phone: t('person.attributes.cell_phone'),
        created_at: t('person.attributes.created_at'),
        email: t('person.attributes.email'),
        cpf: t('person.attributes.cpf'),
        expires_at: t('person.attributes.expires_at'),
        born_date: t('person.attributes.born_date'),
        has_benefits_club: t('person.attributes.has_benefits_club'),
        last_access: t('person.attributes.last_access'),
        uuid: t('person.attributes.uuid'),
        image_base64: t('person.attributes.image_base64'),
        phone_country_prefix: t('person.attributes.phone_country_prefix'),
        reason_to_use: t('person.attributes.reason_to_use'),
        signature: t('person.attributes.signature'),
        platform: t('person.attributes.platform'),
        nationality: t('person.attributes.nationality'),
        doctor_interest: t('person.attributes.doctor_interest'),
        device_language: t('person.attributes.device_language'),
        store_location: t('person.attributes.store_location'),
        app_version: t('person.attributes.app_version'),
        owner_partner: t('person.attributes.owner_partner'),
        packs_enable: t('person.attributes.packs_enable'),
        specialties: t('person.attributes.specialties'),
        doctor_specialties: t('person.attributes.doctor_specialties'),
        password: t('person.attributes.password'),
        role: t('person.attributes.role'),
        signup_messages: t('person.attributes.signup_messages'),
        device_info: t('person.attributes.device_info'),
        partner_id: t('person.attributes.partner_id'),
        partner: t('person.attributes.partner_id'),
        partner_supplier_id: t('person.attributes.partner_supplier_id'),
        partner_supplier: t('person.attributes.partner_supplier_id'),
        corporate_partner_id: t('person.attributes.corporate_partner'),
        corporate_partner: t('person.attributes.corporate_partner'),
        postal_code: t('person.attributes.postal_code'),
        neighborhood: t('person.attributes.neighborhood'),
        street: t('person.attributes.street'),
        number: t('person.attributes.number'),
        city: t('person.attributes.city'),
        crm: t('person.attributes.crm'),
        crp: t('person.attributes.crp'),
        doctor: t('person.roles.doctor'),
        attendant: t('person.roles.attendant'),
        partner_role: t('person.roles.partner_role'),
        admin: t('person.roles.admin'),
        customer: t('person.roles.customer'),
        marketing: t('person.roles.marketing'),
        packPackages: t('person.attributes.packPackages'),
        gender: t('person.attributes.gender'),
        cns: t('person.attributes.cns'),
        is_regulator: t('person.attributes.is_regulator'),
        reports_only: t('person.attributes.reports_only'),
      };
    }

    return this._attributeNames;
  }

  toParams(attrs = {}) {
    const {
      name,
      email,
      cpf,
      reason_to_use,
      nationality,
      expires_at,
      born_date,
      has_benefits_club,
      enabled_benefits_club,
      signup_messages,
      specialties,
      specialty_durations,
      device_info,
      role,
      crm,
      crp,
      crm_numero,
      crm_conselho,
      crm_uf,
      gender,
      cns,
      is_regulator,
      reports_only,
    } = this.toObject();

    const password = this.get('password');
    const cellPhone = onlyNumbers(this.get('cell_phone'));

    attrs.cell_phone = cellPhone;

    if (cellPhone.length > 11) {
      attrs.cell_phone = cellPhone.substring(2, cellPhone.length);
      attrs.phone_country_prefix = cellPhone.substring(0, 2);
    }

    if (password) {
      attrs.password = password;
    }

    let specialtiesIds = [];
    if (specialties) {
      specialtiesIds = specialties.map((item) => item.id);
    }

    const specialtyDurations = {};
    specialtiesIds.forEach((id) => {
      specialtyDurations[id] = (specialty_durations || {})[id] || null;
    });

    return {
      data: {
        attributes: {
          name,
          email,
          cpf,
          reason_to_use,
          nationality,
          expires_at,
          born_date,
          enabled_benefits_club,
          has_benefits_club,
          role,
          signup_messages,
          specialties: specialtiesIds,
          specialty_durations: specialtyDurations,
          device_info,
          crm,
          crp,
          crm_numero,
          crm_conselho,
          crm_uf,
          gender,
          cns,
          is_regulator: !!is_regulator,
          reports_only: !!reports_only,
          owner_partner_id: dig(this.get('owner_partner'), 'id') || null,
          partner_id: dig(this.get('partner'), 'id') || null,
          partner_supplier_id: dig(this.get('partner_supplier'), 'id') || null,
          corporate_partner_id:
            dig(this.get('corporate_partner'), 'id') || null,
          address_id: dig(this.get('address'), 'id') || null,
          postal_code: dig(this.get('address'), 'postal_code') || null,
          city_id: dig(this.get('address'), 'city', 'id') || null,
          neighborhood: dig(this.get('address'), 'neighborhood') || null,
          street: dig(this.get('address'), 'street') || null,
          number: dig(this.get('address'), 'number') || null,
          ...attrs,
        },
      },
    };
  }
}
